/**
 * ModalContext - Global Modal State Management
 * 
 * Tracks when any modal is open to allow other components (like LegalFooter)
 * to adjust their behavior accordingly (e.g., hide or change position).
 * 
 * This prevents modals from being obscured by fixed/sticky UI elements.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const ModalContext = createContext(null);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    console.warn('[ModalContext] useModal called outside of ModalProvider');
    return {
      isAnyModalOpen: false,
      activeModals: [],
      registerModal: () => () => {},
      openModal: () => {},
      closeModal: () => {}
    };
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  // Track all currently open modals by their unique IDs
  const [activeModals, setActiveModals] = useState([]);

  // Register a modal - returns a cleanup function
  const registerModal = useCallback((modalId) => {
    setActiveModals(prev => {
      if (prev.includes(modalId)) return prev;
      console.log('[ModalContext] Modal opened:', modalId);
      return [...prev, modalId];
    });

    // Return cleanup function to unregister
    return () => {
      setActiveModals(prev => {
        console.log('[ModalContext] Modal closed:', modalId);
        return prev.filter(id => id !== modalId);
      });
    };
  }, []);

  // Imperative open/close methods
  const openModal = useCallback((modalId) => {
    setActiveModals(prev => {
      if (prev.includes(modalId)) return prev;
      console.log('[ModalContext] Modal opened:', modalId);
      return [...prev, modalId];
    });
  }, []);

  const closeModal = useCallback((modalId) => {
    setActiveModals(prev => {
      console.log('[ModalContext] Modal closed:', modalId);
      return prev.filter(id => id !== modalId);
    });
  }, []);

  const isAnyModalOpen = activeModals.length > 0;

  const value = {
    isAnyModalOpen,
    activeModals,
    registerModal,
    openModal,
    closeModal
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

export default ModalContext;
